import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(ChartDataLabels);

const LITVIK_COLORS = [
  '#1a3f8f', '#2563eb', '#0f255f', '#1d4ed8', '#3b82f6',
  '#1e40af', '#1e3a8a', '#4f78db', '#5b8def', '#0369a1',
];

/**
 * Bar chart of summary totals by entity (plant / marketing / customer / vehicle).
 * Expects rows with `label` + numeric `total` (Total row excluded by caller).
 */
function DispatchSummaryChart({
  rows = [],
  title = 'Summary',
  period = '',
  valueLabel = 'Qty',
}) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!rows.length) {
      return undefined;
    }

    const labels = rows.map((row) => row.label);
    const values = rows.map((row) => Number(row.total || 0));
    const ctx = canvas.getContext('2d');

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label: valueLabel,
          data: values,
          backgroundColor: labels.map((_, index) => LITVIK_COLORS[index % LITVIK_COLORS.length]),
          borderColor: '#0a1a40',
          borderWidth: 1,
          borderRadius: 6,
          maxBarThickness: 52,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 18, right: 8, bottom: 4, left: 4 },
        },
        plugins: {
          legend: { display: false },
          title: { display: false },
          tooltip: {
            backgroundColor: '#0f255f',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            titleFont: { weight: '700', size: 13 },
            bodyFont: { weight: '700', size: 13 },
            padding: 10,
            callbacks: {
              label: (context) => `${valueLabel}: ${Number(context.raw || 0).toFixed(2)}`,
            },
          },
          datalabels: {
            anchor: 'end',
            align: 'top',
            offset: 2,
            clamp: true,
            color: '#061228',
            backgroundColor: 'rgba(255, 255, 255, 0.92)',
            borderColor: '#1a3f8f',
            borderWidth: 1,
            borderRadius: 4,
            padding: { top: 2, bottom: 2, left: 5, right: 5 },
            font: {
              weight: '800',
              size: 12,
              family: 'Segoe UI, Arial, sans-serif',
            },
            formatter: (value) => {
              const num = Number(value || 0);
              if (!num) {
                return '';
              }
              return Number.isInteger(num) ? String(num) : num.toFixed(2);
            },
          },
        },
        scales: {
          x: {
            ticks: {
              color: '#061228',
              font: { weight: '700', size: 12 },
              maxRotation: 45,
              minRotation: 0,
              autoSkip: true,
              maxTicksLimit: 16,
            },
            grid: { display: false },
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: '#061228',
              font: { weight: '700', size: 12 },
            },
            grid: { color: 'rgba(26, 63, 143, 0.18)' },
            suggestedMax: Math.max(...values, 0) * 1.18 || 1,
          },
        },
      },
      plugins: [ChartDataLabels],
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [rows, valueLabel]);

  if (!rows.length) {
    return null;
  }

  return (
    <div
      className="dispatch-summary-chart"
      style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 55%, #e8eef9 100%)',
        border: '1px solid #9db7e0',
        borderRadius: 12,
        padding: '14px 16px 10px',
        marginBottom: 16,
        boxShadow: '0 8px 20px rgba(15, 37, 95, 0.08)',
      }}
    >
      <div style={{ marginBottom: 8 }}>
        <div
          style={{
            color: '#061228',
            fontWeight: 800,
            fontSize: 18,
            lineHeight: 1.25,
            fontFamily: 'Segoe UI, Arial, sans-serif',
          }}
        >
          {title}
        </div>
        {period ? (
          <div
            style={{
              marginTop: 4,
              color: '#1a3f8f',
              fontWeight: 700,
              fontSize: 14,
              lineHeight: 1.3,
              fontFamily: 'Segoe UI, Arial, sans-serif',
            }}
          >
            Period: {period}
          </div>
        ) : null}
      </div>
      <div style={{ height: 280 }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
}

export default DispatchSummaryChart;
